// src/pages/ApplySuccessPage.js
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaCalendarAlt, FaEnvelope, FaArrowLeft, FaHome } from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';

const ApplySuccessPage = () => {
  const location = useLocation();
  const applicationData = location.state?.applicationData || {};
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <>
      <Helmet>
        <title>Application Submitted - Afrovate Tech Hub</title>
        <meta name="description" content="Thank you for applying to Afrovate Tech Hub's training programs. Your application has been successfully submitted." />
      </Helmet>
      
      {/* Success Hero Section */}
      <Section background="gradient-primary" padding="lg" className="pt-32">
        <div className="text-center">
          <div className="bg-white h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-green-600 text-4xl" />
          </div>
          
          <Heading level={1} color="white" align="center" className="mb-6">
            Application Submitted Successfully!
          </Heading>
          <p className="text-xl text-white text-opacity-90 max-w-3xl mx-auto">
            Thank you for applying to Afrovate Tech Hub's training programs. We have received your application.
          </p>
        </div>
      </Section>
      
      {/* Main Content */}
      <Section background="white" padding="lg">
        <motion.div 
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-green-50 border-2 border-green-100 rounded-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              {applicationData.programName ? (
                <>Thank you for applying to the <strong>{applicationData.programName}</strong> program.</>
              ) : (
                <>Thank you for applying to our training program.</>
              )}
              {' '}We have received your application and will review it shortly.
            </p>
            
            <div className="bg-white rounded-lg p-6 mb-6">
              <div className="flex items-center mb-4">
                <FaCalendarAlt className="text-primary mr-3" />
                <div>
                  <h4 className="font-bold">Next Steps</h4>
                  <p className="text-gray-600">We will contact you via email or phone within 5-7 business days regarding your application status.</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <FaEnvelope className="text-primary mr-3" />
                <div>
                  <h4 className="font-bold">Confirmation Email</h4>
                  <p className="text-gray-600">
                    {applicationData.email ? (
                      <>A confirmation email has been sent to <strong>{applicationData.email}</strong> with your application details.</>
                    ) : (
                      <>A confirmation email has been sent to your email address with your application details.</>
                    )}
                  </p>
                </div>
              </div>
            </div>
            
            <p className="text-gray-600 mb-8">
              If you have any questions, please don't hesitate to contact us at{' '}
              <a href="mailto:info@leotechsl.com" className="text-primary font-medium">
                info@leotechsl.com
              </a>.
            </p>
          </div>
          
          {/* Application Details Summary */}
          {Object.keys(applicationData).length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <h3 className="text-xl font-bold mb-4">Application Summary</h3>
              <div className="border-t border-gray-200 pt-4">
                {applicationData.firstName && applicationData.lastName && (
                  <div className="flex py-2 border-b border-gray-100">
                    <div className="w-1/3 font-medium">Name:</div>
                    <div className="w-2/3">{applicationData.firstName} {applicationData.lastName}</div>
                  </div>
                )}
                
                {applicationData.email && (
                  <div className="flex py-2 border-b border-gray-100">
                    <div className="w-1/3 font-medium">Email:</div>
                    <div className="w-2/3">{applicationData.email}</div>
                  </div>
                )}
                
                {applicationData.phone && (
                  <div className="flex py-2 border-b border-gray-100">
                    <div className="w-1/3 font-medium">Phone:</div>
                    <div className="w-2/3">{applicationData.phone}</div>
                  </div>
                )}
                
                {applicationData.programName && (
                  <div className="flex py-2 border-b border-gray-100">
                    <div className="w-1/3 font-medium">Program:</div>
                    <div className="w-2/3">{applicationData.programName}</div>
                  </div>
                )}
                
                {applicationData.applicationId && (
                  <div className="flex py-2">
                    <div className="w-1/3 font-medium">Application ID:</div>
                    <div className="w-2/3">{applicationData.applicationId}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              variant="outline-primary"
              size="lg"
              to="/"
              icon={<FaHome />}
              iconPosition="left"
            >
              Return to Homepage
            </Button>
            <Button
              variant="primary"
              size="lg"
              to="/programs"
              icon={<FaArrowLeft />}
              iconPosition="left"
            >
              Explore Programs
            </Button>
          </div>
        </motion.div>
      </Section>
      
      {/* What's Next Section */}
      <Section background="gray" padding="normal">
        <div className="max-w-4xl mx-auto text-center">
          <Heading level={2} align="center" className="mb-8" withAccent>
            What Happens Next?
          </Heading>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="bg-primary text-white h-12 w-12 flex items-center justify-center rounded-full mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-bold mb-2">Application Review</h3>
              <p className="text-gray-600">
                Our team will carefully review your application to ensure you're a good fit for the program.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="bg-primary text-white h-12 w-12 flex items-center justify-center rounded-full mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-bold mb-2">Confirmation Email</h3>
              <p className="text-gray-600">
                If accepted, you'll receive a confirmation email with enrollment instructions and payment details.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="bg-primary text-white h-12 w-12 flex items-center justify-center rounded-full mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-bold mb-2">Orientation Session</h3>
              <p className="text-gray-600">
                Attend our orientation session to meet your instructors and learn more about the program.
              </p>
            </div>
          </div>
          
          <p className="text-lg text-gray-600">
            We're excited to have you join the Leotech community! Get ready to embark on a journey to acquire marketable skills that will transform your career prospects.
          </p>
        </div>
      </Section>
    </>
  );
};

export default ApplySuccessPage;